import * as dateFns from 'date-fns';

import { TeamActivity, TeamDuty } from '@pdcplan/shared/api/TeamPlan';
import { addDays, startOfDay } from 'date-fns';

import { da } from 'date-fns/locale/da';
import { enGB } from 'date-fns/locale/en-GB';
import { sv } from 'date-fns/locale/sv';

export type Period = {
  from: Date;
  to: Date;
};

export function getDateFromDutyline(input: number) {
  return new Date(new Date(0).setHours(input / 60, input % 60, 0, 0));
}
export function getTimeStringFromNumber(input: number, isEnd = false): string {
  switch (input) {
    case -1:
    case 9999:
      return '';
    case 1440:
    // @ts-expect-error - falls through
    case 2880:
      if (isEnd) {
        return `24.00`;
      }
    // TODO Here result becomes undefined!
    // falls through
    default:
      if (input >= 1440) {
        return dateFns.format(getDateFromDutyline(input), `HH.mm`) + '(+1)';
      }
      return dateFns.format(getDateFromDutyline(input), `HH.mm`);
  }
}
export function formatActivityOrDutylineTime(
  dutyline: TeamDuty | TeamActivity
) {
  if (dutyline.start === null || dutyline.end === null) {
    return getTimeStringFromNumber(dutyline.start ?? dutyline.end ?? -1);
  }
  return (
    getTimeStringFromNumber(dutyline.start) +
    ' - ' +
    getTimeStringFromNumber(dutyline.end, true)
  );
}

/**
 * Like localeFormat, but with long day names, and without the first letter capitalized
 */
export function localeFormatLongDay(
  date: Date,
  showYear: boolean,
  locale = ''
) {
  let returnString = '';
  switch (locale) {
    case 'Da':
      returnString = dateFns
        .format(date, 'eeee dd. MMM', {
          locale: da,
        })
        .slice(0, -1);
      break;
    case 'Se':
      returnString = dateFns.format(date, 'eeee dd. MMM', {
        locale: sv,
      });
      break;
    default:
      returnString = dateFns.format(date, 'eeee Do MMM', {
        locale: enGB,
      });
  }
  if (showYear) {
    returnString += dateFns.format(date, ' yyyy');
  }
  return returnString;
}

export function dateToString(date: Date): string {
  return dateFns.format(date, 'yyyy-MM-dd');
}

export function dateTimeToString(date: Date): string {
  return dateFns.format(date, 'yyyy-MM-ddTHH:mm');
}
export function dateToTimeString(date: Date): string {
  return dateFns.format(date, 'HH:mm');
}

export function dateFromString(date: string): Date {
  return new Date(date);
}

export function clonePeriod(period: Period): Period {
  return {
    from: period.from,
    to: period.to,
  };
}

export function combinePeriods(period1: Period, period2: Period): Period {
  const combinedPeriod = {
    from: period1.from,
    to: period1.to,
  };
  if (dateFns.differenceInCalendarDays(period1.from, period2.from) > 0) {
    combinedPeriod.from = period2.from;
  } else if (dateFns.differenceInCalendarDays(period1.to, period2.to) < 0) {
    combinedPeriod.to = period2.to;
  }
  return combinedPeriod;
}

const BEGINNING_OF_TIME = new Date(1900, 0, 3);
const END_OF_TIME = new Date(2100, 0, 3);

function isBorderOfTime(d: Date): boolean {
  return d === BEGINNING_OF_TIME || d === END_OF_TIME;
}
function weekday(date: Date): number {
  return (date.getDay() + 6) % 7;
}
function plusDays(day: Date, days: number): Date {
  return isBorderOfTime(day)
    ? day
    : new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate() + days,
        day.getHours(),
        day.getMinutes()
      );
}
export function floorDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function floorWeek(day: Date): Date {
  return plusDays(day, -weekday(day));
}

export function toDefaultLoadPeriod(from: Date, activeScope: number): Period {
  from = startOfDay(from);
  return {
    from,
    to: addDays(from, activeScope),
  };
}

export function isSameDayMidnightInclusive(from: Date, to: Date) {
  return (
    dateFns.isSameDay(from, to) ||
    (dateFns.isSameDay(addDays(from, 1), to) &&
      dateFns.getHours(to) === 0 &&
      dateFns.getMinutes(to) === 0)
  );
}

